import type { WelcomeAction } from "@keydials/api-client";

/**
 * What pressing a welcome-page button does (KEY-814), worked out without touching the router so it can be tested on
 * its own: start a walkthrough, go somewhere in the app, switch the dial's tab, or open a web page in a new tab.
 * Null for a button this app can't act on — the server checks every target on save, so that only happens when an
 * older app meets a newer kind.
 */
export type WelcomeStep =
  | { type: "guide"; guideId: string }
  | { type: "navigate"; to: string }
  | { type: "tab"; tab: string }
  | { type: "link"; href: string };

export function welcomeStep(action: WelcomeAction, instanceId: string): WelcomeStep | null {
  const target = action.target.trim();
  switch (action.kind) {
    case "guide":
      return { type: "guide", guideId: target };
    case "dialSettings":
      return { type: "navigate", to: `/instances/${instanceId}/settings/${encodeURIComponent(target)}` };
    case "accountSettings":
      return { type: "navigate", to: `/settings#${encodeURIComponent(target)}` };
    case "dialTab":
      return { type: "tab", tab: target };
    case "link":
      return /^https?:\/\//i.test(target) ? { type: "link", href: target } : null;
    default:
      return null;
  }
}

/**
 * Whether this person can use the button. Opening a dial Settings section other than the ones every member has is
 * an admin's job, so a non-admin editor isn't offered a door that would show them nothing. Everything else is open
 * to anyone the page is shown to.
 */
export function welcomeActionUsable(action: WelcomeAction, opts: { isDialAdmin: boolean; canAddCalendar: boolean }): boolean {
  if (action.kind !== "dialSettings") return true;
  if (action.target === "calendars") return opts.canAddCalendar;
  if (action.target === "members" || action.target === "display" || action.target === "device") return true;
  return opts.isDialAdmin;
}
